import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { wizard } from "@msys/question-isomorphic";
import {
  CardContainer,
  CollapseSection,
  DurationType,
  getDurationInSeconds,
  getNormalizedDuration,
  LabeledDateValue,
  LabeledSelectMultipleValue,
  LabeledSelectValue,
  LabeledValue,
  Modal,
  RichTextEditor,
  TagChips,
  TextWithBreaks,
} from "@msys/ui";
import {
  ContentCopy as ContentCopyIcon,
  InfoOutlined as InfoOutlinedIcon,
  Settings as SettingsIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Divider,
  FilledInput,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { convertFromRaw, RawDraftContentState } from "draft-js";
import { Form, Formik } from "formik";
import { isEqual, omit, uniqueId, xor } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import {
  ContractType,
  ImplementsTemplateTypePropertiesStrategy,
  namedOperations,
  ProjectType,
  QuoteMaterialPriceHandling,
  QuoteTemplateMaterialMarginRangeInput,
  TplApplicableFor,
  TypeOfTemplate,
  VatRateType,
} from "../../../../clients/graphqlTypes.js";
import { getEmbeddableWizardUrl } from "../../../../environment.js";
import { useUserData } from "../../../auth/useUserData.js";
import { CheckboxGroupField } from "../../../commons/form-fields/CheckboxField.js";
import { ContractTypeRadioGroupField } from "../../../commons/form-fields/ContractTypeRadioGroupField.js";
import { FormattedFloatField } from "../../../commons/form-fields/FormattedFloatField.js";
import { FormattedPercentageField } from "../../../commons/form-fields/FormattedPercentageField.js";
import { RadioBooleanField } from "../../../commons/form-fields/RadioBooleanField.js";
import { RadioGroupField } from "../../../commons/form-fields/RadioGroupField.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { SelectMultipleField } from "../../../commons/form-fields/SelectMultipleField.js";
import { TagsField } from "../../../commons/form-fields/TagsField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import {
  buildDocPath,
  cleanHTML,
  draftStateToHtml,
  DURATION_MAX_VALUES,
} from "../../../utils.js";
import {
  getVatValues,
  useVatFieldValidationSchema,
  VatField,
  VatFieldFormValues,
} from "../../../vat/form-fields/VatField.js";
import { copyTextToClipboard } from "../../addresses/helpers.js";
import { CategoryInput } from "../../categories/CategoryInput.js";
import { MaterialMarginRangesSection } from "../../organisations/form-fields/MaterialMarginRangesSection.js";
import { OrganisationAvatar } from "../../organisations/OrganisationAvatar.js";
import { UserAvatar } from "../../users/UserAvatar.js";
import { useMaterialMarginRangesFieldValidationSchema } from "../../organisations/useMaterialMarginRangesFieldValidationSchema.js";
import { useOrganisationTags } from "../../organisations/useOrganisationTags.js";
import { useQuoteMaterialPriceHandlingOptions } from "../../organisations/useQuoteMaterialPriceHandlingOptions.js";
import { useProjectTypes } from "../../projects/useProjectTypes.js";
import { TemplateTypeAtVersionWarningButton } from "../../templateTypes/TemplateTypeAtVersionWarningButton.js";
import { TemplateTypeInput } from "../../templateTypes/TemplateTypeInput.js";
import {
  getVerticalFormValues,
  getVerticalInputValues,
  useVerticalsFieldValidationSchema,
  VerticalFormValues,
  VerticalsFormField,
} from "../../verticals/form-fields/VerticalsFormField.js";
import { QuoteTemplateUpdateAllLinkingVersionsButton } from "./buttons/QuoteTemplateUpdateAllLinkingVersionsButton.js";
import {
  TemplatesQuoteTemplateSettingsModal_ContainedInTemplateListFragment,
  TemplatesQuoteTemplateSettingsModal_LinkingTemplatesFragment,
  TemplatesQuoteTemplateSettingsModal_QuoteTemplateFragment,
  TemplatesQuoteTemplateSettingsModal_RecommendingTemplatesFragment,
  useImplementsTemplateTypeMutation,
  useModifyQuoteTemplateMutation,
  useModifyQuoteTemplatePublishingInfoMutation,
  useModifyTplApplicableForMutation,
  useTemplatesQuoteTemplateEditSettingsModalQuery,
  useTemplatesQuoteTemplateSettingsModal_ModifyItemInfoMutation,
  useTemplatesQuoteTemplateVersionSettingsModalQuery,
} from "./TemplatesQuoteTemplateSettingsModal.generated.js";
import {
  TemplateTypePropertiesProcessRef,
  TemplateTypeRemovePropertiesProcess,
} from "./TemplateTypePropertiesStrategyModal.js";
import { useTemplateAvaliableFor } from "./useTemplateAvailableFor.js";
import { useTypeOfTemplates } from "./useTypeOfTemplates.js";

export const TemplatesQuoteTemplateEditSettingsModal = ({
  templateId,
  handleClose,
  canEdit,
  pathToDocList,
  refetchQueries,
}: {
  templateId: string;
  handleClose: () => void;
  canEdit?: boolean;
  pathToDocList: string;
  refetchQueries?: string[];
}) => {
  const client = useApolloClient();
  const query = useTemplatesQuoteTemplateEditSettingsModalQuery({
    client,
    variables: { id: templateId },
  });

  const template = getDataOrNull(
    query.data?.quoteTemplateLatest
  )?.quoteTemplate;
  const linkingTemplates =
    getDataOrNull(query.data?.templateLinkingItemsToTemplate)?.templates ?? [];
  const recommendingTemplates =
    getDataOrNull(query.data?.quoteTemplateRecommendedIn)?.templates ?? [];
  const templateListsContainTemplate =
    getDataOrNull(query.data?.templateListsContainTemplate)?.edges.map(
      e => e.node
    ) ?? [];

  const organisationDefaults = getDataOrNull(query.data?.organisationDefaults);
  const defaultVerticalSetting = React.useMemo(
    () =>
      organisationDefaults?.defaultVerticalSettings.find(
        s => s.vertical === "general"
      ),
    [organisationDefaults]
  );

  return (
    <TemplatesQuoteTemplateSettingsModal
      template={template}
      linkingTemplates={linkingTemplates}
      defaultVerticalSetting={defaultVerticalSetting}
      pathToDocList={pathToDocList}
      refetchQueries={refetchQueries}
      handleClose={handleClose}
      canEdit={canEdit}
      isLoading={query.loading}
      recommendingTemplates={recommendingTemplates}
      templateListsContainTemplate={templateListsContainTemplate}
    />
  );
};

export const TemplatesQuoteTemplateVersionSettingsModal = ({
  templateId,
  versionNumber,
  handleClose,
  pathToDocList,
  refetchQueries,
}: {
  templateId: string;
  versionNumber: number;
  handleClose: () => void;
  pathToDocList: string;
  refetchQueries?: string[];
}) => {
  const client = useApolloClient();
  const query = useTemplatesQuoteTemplateVersionSettingsModalQuery({
    client,
    variables: {
      id: templateId,
      versionNumber,
      applyOverrides: false,
    },
  });

  const recommendingTemplates =
    getDataOrNull(query.data?.quoteTemplateRecommendedIn)?.templates ?? [];
  const templateListsContainTemplate =
    getDataOrNull(query.data?.templateListsContainTemplate)?.edges.map(
      e => e.node
    ) ?? [];
  const template = getDataOrNull(
    query.data?.quoteTemplateVersion
  )?.quoteTemplate;
  const linkingTemplates =
    getDataOrNull(query.data?.templateLinkingItemsToTemplate)?.templates ?? [];

  return (
    <TemplatesQuoteTemplateSettingsModal
      template={template}
      linkingTemplates={linkingTemplates}
      pathToDocList={pathToDocList}
      refetchQueries={refetchQueries}
      handleClose={handleClose}
      canEdit={false}
      isLoading={query.loading}
      recommendingTemplates={recommendingTemplates}
      templateListsContainTemplate={templateListsContainTemplate}
    />
  );
};

interface Props {
  template:
    | TemplatesQuoteTemplateSettingsModal_QuoteTemplateFragment
    | undefined
    | null;
  canEdit?: boolean;
  refetchQueries?: string[];
  handleClose: () => void;
  defaultVerticalSetting?: {
    averageWorkBuyingPrice: number;
    averageWorkSellingPrice: number;
  };
  pathToDocList: string;
  isLoading: boolean;
  linkingTemplates: TemplatesQuoteTemplateSettingsModal_LinkingTemplatesFragment[];
  recommendingTemplates: TemplatesQuoteTemplateSettingsModal_RecommendingTemplatesFragment[];
  templateListsContainTemplate: TemplatesQuoteTemplateSettingsModal_ContainedInTemplateListFragment[];
}

interface FormValues extends VatFieldFormValues {
  contractType: ContractType;
  durationAmount: number;
  durationType: DurationType;
  title: string;
  publicDescription: string;
  templateAiDescription: string;
  templateWizardDescription: string;
  defaultMaterialMargin: number;
  materialMarginRanges: QuoteTemplateMaterialMarginRangeInput[];
  materialPriceHandling: QuoteMaterialPriceHandling;
  defaultVerticalSettings: VerticalFormValues[];
  workFactor: number;
  materialFactor: number;
  category: string | null;
  tplApplicableFor: TplApplicableFor[];
  tags: { type: "CUSTOM" | "SYSTEM"; value: string }[];
  implementsTemplateType: {
    id: string;
    title: string;
    revision: number;
  } | null;
  implementsTemplateTypePropertiesStrategy: ImplementsTemplateTypePropertiesStrategy;
  typeOfTemplate: TypeOfTemplate | null;
  typeOfProject: ProjectType[];
  options: {
    applyMaterialMarginToExistingDocument: boolean;
  };
}

const TemplatesQuoteTemplateSettingsModal = ({
  template: doc,
  refetchQueries,
  canEdit = false,
  handleClose,
  defaultVerticalSetting,
  pathToDocList,
  linkingTemplates,
  recommendingTemplates,
  templateListsContainTemplate,
  isLoading,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const viewer = useUserData().currentUser!;

  const { t } = useTranslate([
    "QuoteEditFooter",
    "Quote",
    "Global",
    "OrganisationSettings",
    "Templates",
    "QuoteItem",
  ]);

  const { customTags } = useOrganisationTags("TEMPLATE");
  const { typeOfTemplatesOptions } = useTypeOfTemplates();
  const { projectTypeOptions } = useProjectTypes();

  const client = useApolloClient();
  const [modifyQuoteTemplate] = useModifyQuoteTemplateMutation({
    client,
    refetchQueries: [
      namedOperations.Query.OrganisationTags,
      ...(refetchQueries ?? []),
    ],
    awaitRefetchQueries: true,
  });
  const [modifyQuoteTemplatePublishingInfo] =
    useModifyQuoteTemplatePublishingInfoMutation({
      client,
    });

  const snippet = React.useMemo(() => {
    if (!doc) return null;

    const wizardCfg: wizard.WizardConfigurationSchemaInput = {
      navigationType: null,
      questionStrategy: {
        type: "questionControl",
        askWhen: "onEmbeddableWizard",
        askWhom: "contractee",
        startingNode: { type: "root" },
      },
      requestInitialAnswers: "false",
      sourceDoc: {
        type: "template",
        templateId: doc.id,
        contractorId: viewer.organisation.id,
      },
    };
    const src = `${getEmbeddableWizardUrl()}?${wizard.wizardConfigurationToUrlSearchQuery(wizardCfg)}`;
    return `<iframe src="${src}" ></iframe>`;
  }, [doc, viewer.organisation.id]);

  const [modifyTplApplicableFor] = useModifyTplApplicableForMutation({
    client,
  });
  const handleChangeTplApplicableFor = async (
    tplApplicableFor: TplApplicableFor[]
  ) => {
    if (!doc) throw new Error("Doc is missing");
    if (!tplApplicableFor) throw new Error("tplApplicableFor is missing");

    await modifyTplApplicableFor({
      variables: {
        input: {
          quoteTemplateId: doc.id,
          values: {
            tplApplicableFor,
          },
        },
      },
    });
  };

  const {
    options: templateApplicableForOptions,
    documents: templateApplicableForDocuments,
  } = useTemplateAvaliableFor();

  const quoteMaterialPriceHandlingOptions =
    useQuoteMaterialPriceHandlingOptions();

  const [implementsTemplateType] = useImplementsTemplateTypeMutation({
    client,
  });
  const [modifyItemInfo] =
    useTemplatesQuoteTemplateSettingsModal_ModifyItemInfoMutation({
      client,
    });

  const initialValues = React.useMemo((): FormValues => {
    const [durationAmount, durationType] = getNormalizedDuration(
      doc?.duration ?? 0
    );
    return {
      contractType: doc?.contractType ?? "fup",
      durationAmount,
      durationType,
      title: doc?.title ?? "",
      publicDescription: doc?.publicDescription ?? "",
      templateAiDescription: doc?.templateAiDescription ?? "",
      templateWizardDescription: doc?.templateWizardDescription ?? "",
      ...getVatValues(
        doc?.vatInfo?.countryCode,
        doc?.vatInfo?.rateType,
        doc?.vatRate
      ),
      defaultMaterialMargin: doc?.defaultMaterialMargin ?? 0.2,
      materialMarginRanges:
        doc?.materialMarginRanges?.map(r => omit(r, "id", "__typename")) ?? [],
      materialPriceHandling:
        doc?.materialPriceHandling ?? "CALCULATED_SELL_PRICE",
      defaultVerticalSettings: getVerticalFormValues(
        doc?.defaultVerticalSettings ?? []
      ),
      workFactor: doc?.workFactor ?? 1,
      materialFactor: doc?.materialFactor ?? 1,
      category: doc?.category?.id ?? null,
      tplApplicableFor: doc?.tplApplicableFor
        ? [...doc.tplApplicableFor]
        : ["QUOTE"],
      tags:
        doc?.customTags.map(tag => ({
          type: "CUSTOM" as const,
          value: tag,
        })) ?? [],
      implementsTemplateType: doc?.implementsTemplateType
        ? {
            id: doc.implementsTemplateType.templateType.id,
            title: doc.implementsTemplateType.templateType.title,
            revision: doc.implementsTemplateType.templateType.revision,
          }
        : null,
      implementsTemplateTypePropertiesStrategy: "keepPropertiesWithMerging",
      typeOfTemplate: doc?.typeOfTemplate ?? null,
      typeOfProject: doc?.typeOfProject ?? [],
      options: {
        applyMaterialMarginToExistingDocument: false,
      },
    };
  }, [doc]);

  const validationSchema = Yup.object().shape({
    durationAmount: Yup.number().label(
      t("Work duration", { ns: "QuoteEditFooter" })
    ),
    title: Yup.string()
      .label(t("Title", { ns: "Quote" }))
      .required(),
    publicDescription: Yup.string().label(
      t("Marketing description of template", { ns: "Templates" })
    ),
    templateAiDescription: Yup.string().label(
      t("AI friendly description of template", { ns: "Templates" })
    ),
    templateWizardDescription: Yup.string().label(
      t("Wizard introductory text of template", { ns: "Templates" })
    ),
    category: Yup.string()
      .label(t("Template category", { ns: "Templates" }))
      .nullable(),
    durationType: Yup.string()
      .label(t("Units", { ns: "QuoteEditFooter" }))
      .oneOf(["days", "weeks", "months", "none"]),
    contractType: Yup.string()
      .label(t("Contract Type", { ns: "QuoteEditFooter" }))
      .required(),
    ...useVatFieldValidationSchema(),
    defaultMaterialMargin: Yup.number()
      .min(0)
      .label(t("Default material margin", { ns: "OrganisationSettings" }))
      .required(),
    materialMarginRanges: useMaterialMarginRangesFieldValidationSchema(
      "materialMarginRanges"
    ),
    materialPriceHandling: Yup.string()
      .label(t("Material price handling", { ns: "QuoteEditFooter" }))
      .required(),
    defaultVerticalSettings: useVerticalsFieldValidationSchema(
      "defaultVerticalSettings"
    ),
    workFactor: Yup.number()
      .label(t("Work factor", { ns: "QuoteEditFooter" }))
      .min(0)
      .required(),
    materialFactor: Yup.number()
      .label(t("Material factor", { ns: "QuoteEditFooter" }))
      .min(0)
      .required(),
    tplApplicableFor: Yup.array()
      .of(Yup.string().oneOf(templateApplicableForDocuments))
      .label(t("Available for creating", { ns: "Templates" }))
      .required()
      .min(1),
    typeOfTemplate: Yup.string()
      .nullable()
      .label(t("Type of template", { ns: "Templates" })),
    typeOfProject: Yup.array()
      .of(Yup.string())
      .label(t("Type of project", { ns: "Templates" })),
    tags: Yup.array()
      .of(Yup.object().shape({ value: Yup.string() }))
      .label(t("Tags", { ns: "QuoteEditFooter" })),
    options: Yup.object().shape({
      applyMaterialMarginToExistingDocument: Yup.boolean(),
    }),
  });

  async function handleSubmit(values: FormValues) {
    if (!doc) throw new Error("Doc is missing");
    if (!canEdit) throw new Error("Not allowed to edit");

    if (
      initialValues.implementsTemplateType !== values.implementsTemplateType
    ) {
      await implementsTemplateType({
        variables: {
          input: {
            templateId: doc.id,
            templateTypeId: values.implementsTemplateType?.id ?? null,
            templateTypePropertiesStrategy:
              values.implementsTemplateTypePropertiesStrategy,
          },
        },
      });
    }

    try {
      if (
        initialValues.publicDescription !== values.publicDescription ||
        initialValues.templateAiDescription !== values.templateAiDescription ||
        initialValues.templateWizardDescription !==
          values.templateWizardDescription
      ) {
        await modifyQuoteTemplatePublishingInfo({
          variables: {
            input: {
              quoteTemplateId: doc.id,
              values: {
                publicDescription: values.publicDescription,
                templateAiDescription: values.templateAiDescription,
                templateWizardDescription: values.templateWizardDescription,
              },
            },
          },
        });
      }

      if (initialValues.title !== values.title) {
        await modifyItemInfo({
          variables: {
            input: {
              itemId: doc.rootItem.id,
              docId: doc.id,
              projectId: null,
              values: { title: values.title },
            },
          },
        });
      }

      if (
        xor(initialValues.tplApplicableFor, values.tplApplicableFor).length > 0
      ) {
        await handleChangeTplApplicableFor(values.tplApplicableFor);
      }

      const applyMaterialMarginToExistingDocument =
        (!isEqual(
          values.defaultMaterialMargin,
          initialValues.defaultMaterialMargin
        ) ||
          !isEqual(
            values.materialMarginRanges,
            initialValues.materialMarginRanges
          )) &&
        values.options.applyMaterialMarginToExistingDocument;

      await modifyQuoteTemplate({
        variables: {
          input: {
            quoteTemplateId: doc.id,
            values: {
              duration: getDurationInSeconds({
                durationAmount: values.durationAmount,
                durationType: values.durationType,
              }),
              contractType: values.contractType,
              vatRate: values.vatRate,
              vatInfo: {
                countryCode: values.vatInfo.countryCode,
                rateType: values.vatInfo.rateType as VatRateType,
              },
              defaultMaterialMargin: values.defaultMaterialMargin,
              materialMarginRanges: values.materialMarginRanges,
              materialPriceHandling: values.materialPriceHandling,
              defaultVerticalSettings: getVerticalInputValues(
                values.defaultVerticalSettings
              ),
              workFactor: values.workFactor,
              materialFactor: values.materialFactor,
              category: values.category,
              typeOfProject: values.typeOfProject,
              typeOfTemplate: values.typeOfTemplate,
              tags: values.tags
                .filter(tag => tag.type === "CUSTOM")
                .map(tag => tag.value),
            },
            options: { applyMaterialMarginToExistingDocument },
          },
          refetchTree:
            initialValues.contractType !== values.contractType ||
            initialValues.workFactor !== values.workFactor ||
            initialValues.materialFactor !== values.materialFactor ||
            initialValues.vatRate !== values.vatRate ||
            !isEqual(
              values.defaultVerticalSettings,
              initialValues.defaultVerticalSettings
            ) ||
            applyMaterialMarginToExistingDocument,
        },
      });
      enqueueSnackbar(t("Settings saved", { ns: "QuoteEditFooter" }));
      handleClose();
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    }
  }

  const isOwnOrganisationTemplate =
    doc?.owningSystemOrganisationId === viewer.organisation.id;
  const hasOutdatedIncomingLinks =
    doc &&
    linkingTemplates.some(template =>
      template.linkingItems.some(
        item =>
          item.linkedQuoteTemplateAtVersionNumber !==
          doc.latestAvailableVersionNumber
      )
    );
  const isLatestPublishedVersion =
    !!doc?.latestPublishedVersionNumber &&
    !!doc?.resolvedAsReadModelVersionNumber &&
    doc.resolvedAsReadModelVersionNumber === doc.latestPublishedVersionNumber;

  const formId = React.useMemo(() => uniqueId(), []);

  const templateTypePropertiesProcessRef =
    React.useRef<TemplateTypePropertiesProcessRef | null>(null);

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {formikProps => (
        <Modal
          title={t("Template info & settings", { ns: "QuoteEditFooter" })}
          maxWidth="md"
          actionButtons={
            canEdit
              ? [
                  {
                    label: t("Cancel", { ns: "Global" }),
                    handleClick: handleClose,
                    buttonProps: { variant: "text" },
                  },
                  {
                    label: t("Save", { ns: "Global" }),
                    buttonProps: {
                      loading: formikProps.isSubmitting,
                      form: formId,
                      type: "submit",
                      disabled: !formikProps.dirty || !formikProps.isValid,
                    },
                  },
                ]
              : []
          }
          handleClose={handleClose}
          isLoading={isLoading}
        >
          {doc && (
            <Form id={formId}>
              <Grid container spacing={1}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Stack spacing={1} direction="column">
                    <CardContainer
                      isExpandable={false}
                      isInitiallyClosed={false}
                      title={t("Details", { ns: "QuoteEditFooter" })}
                      Icon={<InfoOutlinedIcon />}
                    >
                      <Stack
                        direction="column"
                        spacing={1}
                        sx={{
                          p: 1,
                        }}
                      >
                        <LabeledValue
                          label={t("Publishing organisation", { ns: "Quote" })}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{
                              alignItems: "center",
                            }}
                          >
                            <OrganisationAvatar
                              organisationAvatar={doc.publishingCompany}
                              size="s"
                            />
                            <Typography variant="body1">
                              {doc.publishingCompany?.title}
                            </Typography>
                          </Stack>
                        </LabeledValue>

                        {isOwnOrganisationTemplate && (
                          <LabeledValue
                            label={t("Created by", { ns: "Global" })}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{
                                alignItems: "center",
                              }}
                            >
                              <UserAvatar userAvatar={doc.author} size="s" />
                              <Typography variant="body1">
                                {doc.author.fullname}
                              </Typography>
                            </Stack>
                          </LabeledValue>
                        )}

                        {doc.resolvedAsReadModelVersionNumber ? (
                          <LabeledDateValue
                            label={t("Published", { ns: "Quote" })}
                            value={doc.resolvedVersionPublishedAt}
                            notSetText="-"
                          />
                        ) : (
                          <LabeledDateValue
                            label={t("Last Published", { ns: "Templates" })}
                            value={doc.latestVersionPublishedAt}
                            notSetText="-"
                          />
                        )}

                        <TemplateTypeInput
                          canEdit={canEdit}
                          templateTypeId={
                            formikProps.values.implementsTemplateType?.id ??
                            null
                          }
                          leftIcon={
                            canEdit &&
                            doc.implementsTemplateType &&
                            formikProps.values.implementsTemplateType &&
                            doc.implementsTemplateType.templateType.id ===
                              formikProps.values.implementsTemplateType.id &&
                            doc.implementsTemplateType.atRevision !==
                              formikProps.values.implementsTemplateType
                                .revision ? (
                              <TemplateTypeAtVersionWarningButton
                                templateTypeRevision={
                                  formikProps.values.implementsTemplateType
                                    .revision
                                }
                                atRevision={
                                  doc.implementsTemplateType.atRevision
                                }
                              />
                            ) : undefined
                          }
                          onChange={async templateType => {
                            if (doc.rootItem?.props2.length) {
                              const strategy =
                                await templateTypePropertiesProcessRef.current?.start(
                                  "add"
                                );
                              if (!strategy) return;
                              await formikProps.setFieldValue(
                                "implementsTemplateTypePropertiesStrategy",
                                strategy
                              );
                            }
                            await formikProps.setFieldValue(
                              "implementsTemplateType",
                              {
                                id: templateType.id,
                                title: templateType.title,
                                revision: templateType.revision,
                              }
                            );
                            await formikProps.setTouched({
                              implementsTemplateType: true,
                            });
                          }}
                          onClear={async () => {
                            if (
                              doc.implementsTemplateType?.templateType.props2
                                .length
                            ) {
                              const strategy =
                                await templateTypePropertiesProcessRef.current?.start(
                                  "remove"
                                );
                              if (!strategy) return;
                              await formikProps.setFieldValue(
                                "implementsTemplateTypePropertiesStrategy",
                                strategy
                              );
                            }
                            await formikProps.setFieldValue(
                              "implementsTemplateType",
                              null
                            );
                            await formikProps.setTouched({
                              implementsTemplateType: true,
                            });
                          }}
                        />

                        <CategoryInput
                          canEdit={canEdit}
                          categoryId={formikProps.values.category}
                          onChange={async category => {
                            await formikProps.setFieldValue(
                              "category",
                              category.id
                            );
                          }}
                          onClear={
                            formikProps.values.category
                              ? async () => {
                                  await formikProps.setFieldValue(
                                    "category",
                                    null
                                  );
                                }
                              : undefined
                          }
                          label={t("Template category", { ns: "Templates" })}
                          selectButtonLabel={t("Add template category", {
                            ns: "Templates",
                          })}
                        />

                        {canEdit ? (
                          <TextField
                            name="title"
                            label={t("Title", { ns: "Quote" })}
                            disabled={!canEdit || formikProps.isSubmitting}
                          />
                        ) : (
                          <LabeledValue label={t("Title", { ns: "Quote" })}>
                            {doc.title}
                          </LabeledValue>
                        )}

                        <RichTextEditor
                          label={t("Marketing description of template", {
                            ns: "Templates",
                          })}
                          showLabelInTextValue
                          placeholder={"–"}
                          htmlContent={formikProps.values.publicDescription}
                          onChange={async (content: RawDraftContentState) => {
                            await formikProps.setFieldValue(
                              "publicDescription",
                              cleanHTML(
                                draftStateToHtml(convertFromRaw(content))
                              )
                            );
                          }}
                          canEdit={canEdit}
                          disabled={formikProps.isSubmitting}
                          saveButtonLabel={t("Save", { ns: "Global" })}
                          cancelButtonLabel={t("Cancel", { ns: "Global" })}
                        />

                        <RichTextEditor
                          label={t("Wizard introductory text of template", {
                            ns: "Templates",
                          })}
                          showLabelInTextValue
                          placeholder={"–"}
                          htmlContent={
                            formikProps.values.templateWizardDescription
                          }
                          onChange={async (content: RawDraftContentState) => {
                            await formikProps.setFieldValue(
                              "templateWizardDescription",
                              cleanHTML(
                                draftStateToHtml(convertFromRaw(content))
                              )
                            );
                          }}
                          canEdit={canEdit}
                          disabled={formikProps.isSubmitting}
                          saveButtonLabel={t("Save", { ns: "Global" })}
                          cancelButtonLabel={t("Cancel", { ns: "Global" })}
                        />

                        {canEdit ? (
                          <TextField
                            name="templateAiDescription"
                            label={t("AI friendly description of template", {
                              ns: "Templates",
                            })}
                            disabled={formikProps.isSubmitting}
                            multiline
                          />
                        ) : (
                          <LabeledValue
                            label={t("AI friendly description of template", {
                              ns: "Templates",
                            })}
                          >
                            {doc.templateAiDescription ? (
                              <TextWithBreaks
                                text={doc.templateAiDescription}
                              />
                            ) : (
                              "–"
                            )}
                          </LabeledValue>
                        )}

                        {canEdit ? (
                          <TagsField
                            inputLabel={t("Tags", { ns: "QuoteEditFooter" })}
                            name="tags"
                            options={customTags}
                            disabled={!canEdit || formikProps.isSubmitting}
                          />
                        ) : (
                          <LabeledValue
                            label={t("Tags", { ns: "QuoteEditFooter" })}
                          >
                            {doc.customTags.length > 0 ? (
                              <TagChips tags={doc.customTags} />
                            ) : (
                              "–"
                            )}
                          </LabeledValue>
                        )}

                        {canEdit ? (
                          <SelectField
                            label={t("Type of template", { ns: "Templates" })}
                            name="typeOfTemplate"
                            options={typeOfTemplatesOptions}
                            disabled={!canEdit || formikProps.isSubmitting}
                            clearable
                          />
                        ) : (
                          <LabeledSelectValue
                            label={t("Type of template", { ns: "Templates" })}
                            options={typeOfTemplatesOptions}
                            value={doc.typeOfTemplate}
                            notSetText={t("Not set", {
                              ns: "Global",
                            })}
                          />
                        )}

                        {canEdit ? (
                          <SelectMultipleField
                            label={t("Type of project", { ns: "Templates" })}
                            name="typeOfProject"
                            options={projectTypeOptions}
                            disabled={!canEdit || formikProps.isSubmitting}
                            clearable
                          />
                        ) : (
                          <LabeledSelectMultipleValue
                            label={t("Type of project", { ns: "Templates" })}
                            options={projectTypeOptions}
                            value={doc.typeOfProject ?? undefined}
                            notSetText={t("Not set", {
                              ns: "Global",
                            })}
                          />
                        )}

                        <Divider />

                        <CheckboxGroupField
                          name="tplApplicableFor"
                          label={t("Available for creating", {
                            ns: "Templates",
                          })}
                          options={templateApplicableForOptions}
                          size="small"
                          disabled={!canEdit || formikProps.isSubmitting}
                        />

                        <Divider />

                        <Typography color="secondary">
                          {t("This template is included in", {
                            ns: "Templates",
                          })}
                        </Typography>

                        {!linkingTemplates.length && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "grey.500",
                              textAlign: "center",
                            }}
                          >
                            {t("No templates", { ns: "QuoteItem" })}
                          </Typography>
                        )}

                        {linkingTemplates.flatMap(linkingTemplate =>
                          linkingTemplate.linkingItems.map(item => (
                            <Stack key={item.id} direction={"row"} spacing={1}>
                              <MuiLink
                                component={Link}
                                to={`${
                                  buildDocPath(
                                    `${pathToDocList}/${linkingTemplate.doc.id}`,
                                    linkingTemplate.doc
                                      .resolvedAsReadModelVersionNumber ?? null
                                  ) +
                                  `${
                                    linkingTemplate.doc
                                      .resolvedAsReadModelVersionNumber
                                      ? ""
                                      : "/edit"
                                  }`
                                }/items/${item.id}`}
                                target="_blank"
                              >
                                {t("{item} in {template}", {
                                  ns: "Templates",
                                  item: `${item.pathForPdf} ${item.title}`,
                                  template: linkingTemplate.doc.title,
                                })}
                              </MuiLink>
                              {item.linkedQuoteTemplateAtVersionNumber !==
                                doc.latestAvailableVersionNumber && (
                                <Tooltip
                                  title={t("New version available", {
                                    ns: "Templates",
                                  })}
                                >
                                  <WarningAmberIcon
                                    color={"warning"}
                                    fontSize={"small"}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          ))
                        )}

                        <Divider />

                        <Typography color="secondary">
                          {t("This template is recommended in", {
                            ns: "Templates",
                          })}
                        </Typography>

                        {!recommendingTemplates.length && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "grey.500",
                              textAlign: "center",
                            }}
                          >
                            {t("No items", { ns: "QuoteItem" })}
                          </Typography>
                        )}

                        {recommendingTemplates.flatMap(recommendingTemplate =>
                          recommendingTemplate.recommendingItems?.map(item => (
                            <Stack key={item.id} direction={"row"} spacing={1}>
                              <MuiLink
                                component={Link}
                                to={`${
                                  buildDocPath(
                                    `${pathToDocList}/${recommendingTemplate.doc.id}`,
                                    recommendingTemplate.doc
                                      .resolvedAsReadModelVersionNumber ?? null
                                  ) +
                                  `${
                                    recommendingTemplate.doc
                                      .resolvedAsReadModelVersionNumber
                                      ? ""
                                      : "/edit"
                                  }`
                                }/items/${item.id}`}
                                target="_blank"
                              >
                                {t("{item} in {template}", {
                                  ns: "Templates",
                                  item: `${item.pathForPdf} ${item.title}`,
                                  template: recommendingTemplate.doc.title,
                                })}
                              </MuiLink>
                            </Stack>
                          ))
                        )}

                        <Divider />

                        <Typography color="secondary">
                          {t("Template lists including this template", {
                            ns: "Templates",
                          })}
                        </Typography>

                        {!templateListsContainTemplate.length && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "grey.500",
                              textAlign: "center",
                            }}
                          >
                            {t("No template lists", { ns: "Templates" })}
                          </Typography>
                        )}

                        {templateListsContainTemplate.map(templateList => (
                          <Stack
                            key={templateList.id}
                            direction={"row"}
                            spacing={1}
                          >
                            <MuiLink
                              component={Link}
                              to={`/templates/lists/${templateList.id}`}
                              target="_blank"
                            >
                              {templateList.title}
                            </MuiLink>
                          </Stack>
                        ))}

                        {isLatestPublishedVersion &&
                          hasOutdatedIncomingLinks && (
                            <Alert
                              variant="outlined"
                              severity="warning"
                              action={
                                <QuoteTemplateUpdateAllLinkingVersionsButton
                                  templateId={doc.id}
                                  pathToDocList={pathToDocList}
                                />
                              }
                            >
                              <AlertTitle>
                                {t("Outdated links!", {
                                  ns: "Templates",
                                })}
                              </AlertTitle>
                              {t(
                                "Some templates are linking to outdated version of this template.",
                                {
                                  ns: "Templates",
                                }
                              )}
                            </Alert>
                          )}
                      </Stack>
                    </CardContainer>
                    <CardContainer
                      isExpandable={true}
                      isInitiallyClosed={true}
                      title={t("Embeddable wizard snippet", {
                        ns: "Templates",
                      })}
                    >
                      <Stack
                        spacing={1}
                        sx={{
                          padding: 1,
                        }}
                      >
                        {snippet && (
                          <FormControl variant="filled">
                            <InputLabel>
                              {t("Snippet code", {
                                ns: "OrganisationSettings",
                              })}
                            </InputLabel>
                            <FilledInput
                              value={snippet}
                              readOnly
                              multiline
                              fullWidth
                              endAdornment={
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={async () => {
                                    if (snippet)
                                      await copyTextToClipboard(snippet);
                                    enqueueSnackbar(
                                      t("Copied to clipboard", {
                                        ns: "Global",
                                      })
                                    );
                                  }}
                                >
                                  <Tooltip
                                    title={t("Copy snippet", {
                                      ns: "OrganisationSettings",
                                    })}
                                  >
                                    <ContentCopyIcon />
                                  </Tooltip>
                                </IconButton>
                              }
                            />
                          </FormControl>
                        )}
                      </Stack>
                    </CardContainer>
                  </Stack>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <CardContainer
                    title={t("Settings", {
                      ns: "Global",
                    })}
                    isExpandable={false}
                    isInitiallyClosed={false}
                    Icon={<SettingsIcon />}
                  >
                    <CollapseSection
                      title={t("Contract Type", {
                        ns: "QuoteEditFooter",
                      })}
                      isInitiallyExpanded={true}
                      sx={{ px: 1 }}
                    >
                      <Stack
                        direction="column"
                        spacing={1}
                        sx={{
                          p: 1,
                        }}
                      >
                        <ContractTypeRadioGroupField
                          name="contractType"
                          disabled={!canEdit || formikProps.isSubmitting}
                        />
                      </Stack>
                    </CollapseSection>

                    {canEdit && <Divider />}

                    {canEdit && (
                      <CollapseSection
                        title={t("Price calculation settings", {
                          ns: "QuoteEditFooter",
                        })}
                        isInitiallyExpanded={true}
                        sx={{ px: 1 }}
                      >
                        <Stack
                          spacing={1}
                          sx={{
                            p: 1,
                          }}
                        >
                          <Typography>
                            {t("Multiplying factors", {
                              ns: "QuoteEditFooter",
                            })}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <FormattedFloatField
                              name="workFactor"
                              label={t("Work factor", {
                                ns: "QuoteEditFooter",
                              })}
                              type="float"
                              min={0}
                              defaultValue={1}
                              disabled={!canEdit || formikProps.isSubmitting}
                            />
                            <FormattedFloatField
                              name="materialFactor"
                              label={t("Material factor", {
                                ns: "QuoteEditFooter",
                              })}
                              type="float"
                              min={0}
                              defaultValue={1}
                              disabled={!canEdit || formikProps.isSubmitting}
                            />
                          </Stack>
                          <Divider />

                          <Typography>
                            {t("Work price settings", {
                              ns: "OrganisationSettings",
                            })}
                          </Typography>
                          <VerticalsFormField
                            name="defaultVerticalSettings"
                            disabled={!canEdit || formikProps.isSubmitting}
                            defaultValue={defaultVerticalSetting}
                          />
                          <Divider />
                          <Typography>
                            {t("Material price settings", {
                              ns: "OrganisationSettings",
                            })}
                          </Typography>
                          <FormattedPercentageField
                            name="defaultMaterialMargin"
                            label={t("Default material margin", {
                              ns: "OrganisationSettings",
                            })}
                            disabled={!canEdit || formikProps.isSubmitting}
                          />
                          <MaterialMarginRangesSection
                            title={t("Custom material margin ranges", {
                              ns: "OrganisationSettings",
                            })}
                            value={formikProps.values.materialMarginRanges}
                            defaultMaterialMargin={
                              formikProps.values.defaultMaterialMargin
                            }
                            name="materialMarginRanges"
                            disabled={!canEdit || formikProps.isSubmitting}
                          />

                          {(!isEqual(
                            formikProps.values.defaultMaterialMargin,
                            initialValues.defaultMaterialMargin
                          ) ||
                            !isEqual(
                              formikProps.values.materialMarginRanges,
                              initialValues.materialMarginRanges
                            )) && (
                            <Alert severity="warning">
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                {t(
                                  "Would you like to apply the changes also to already existing items in document?",
                                  { ns: "OrganisationSettings" }
                                )}
                              </Typography>
                              <RadioBooleanField
                                name="options.applyMaterialMarginToExistingDocument"
                                trueLabel={t("Yes, apply to existing", {
                                  ns: "OrganisationSettings",
                                })}
                                falseLabel={t("No", { ns: "Global" })}
                              />
                            </Alert>
                          )}

                          <RadioGroupField
                            label={t("Select material price handling option", {
                              ns: "OrganisationSettings",
                            })}
                            name={"materialPriceHandling"}
                            options={quoteMaterialPriceHandlingOptions}
                            condensed
                          />
                        </Stack>
                      </CollapseSection>
                    )}

                    <Divider />

                    <CollapseSection
                      title={t("Offer conditions", {
                        ns: "QuoteEditFooter",
                      })}
                      isInitiallyExpanded={true}
                      sx={{ px: 1 }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          p: 1,
                        }}
                      >
                        <FormattedFloatField
                          name="durationAmount"
                          label={t("Work duration", {
                            ns: "QuoteEditFooter",
                          })}
                          type="float"
                          min={0}
                          max={
                            DURATION_MAX_VALUES[formikProps.values.durationType]
                          }
                          disabled={!canEdit || formikProps.isSubmitting}
                        />
                        <SelectField
                          name="durationType"
                          label={t("Units", {
                            ns: "QuoteEditFooter",
                          })}
                          disabled={!canEdit || formikProps.isSubmitting}
                          options={[
                            {
                              label: "–",
                              value: "none",
                            },
                            {
                              label: t("Days", {
                                ns: "QuoteEditFooter",
                              }),
                              value: "days",
                            },
                            {
                              label: t("Weeks", {
                                ns: "QuoteEditFooter",
                              }),
                              value: "weeks",
                            },
                            {
                              label: t("Months", {
                                ns: "QuoteEditFooter",
                              }),
                              value: "months",
                            },
                          ]}
                        />
                      </Stack>
                    </CollapseSection>

                    <Divider />

                    <CollapseSection
                      title={t("VAT settings", {
                        ns: "QuoteEditFooter",
                      })}
                      isInitiallyExpanded={true}
                      sx={{ px: 1 }}
                    >
                      <Stack
                        direction="column"
                        spacing={1}
                        sx={{
                          p: 1,
                        }}
                      >
                        <VatField
                          disabled={!canEdit || formikProps.isSubmitting}
                        />
                      </Stack>
                    </CollapseSection>
                  </CardContainer>
                </Grid>
              </Grid>
              <TemplateTypeRemovePropertiesProcess
                ref={templateTypePropertiesProcessRef}
              />
            </Form>
          )}
        </Modal>
      )}
    </Formik>
  );
};
